import api from './api';
import { CallLog, CallStatistics, Tender } from '../types';

export interface TenderAwardee {
  id: number;
  name: string;
  normalized_name?: string;
  awardee_city?: string | null;
  awardee_phone?: string | null;
  awardee_email?: string | null;
  notes?: string | null;
  tender_count?: number;
  created_at?: string;
  updated_at?: string;
  tenders?: Array<{
    id: number;
    date: string;
    awarder?: string;
    awardee_name?: string;
    city?: string;
    conversion_status?: string;
    created_at?: string;
  }>;
}

export interface TenderAwardeeSearchHit {
  id: number;
  name: string;
  awardee_city?: string | null;
  awardee_phone?: string | null;
  awardee_email?: string | null;
  tender_count?: number;
}

export interface CreateTenderAwardeeData {
  name: string;
  awardee_city?: string;
  awardee_phone?: string;
  awardee_email?: string | null;
  notes?: string;
}

export interface UpdateTenderAwardeeData {
  name?: string;
  awardee_city?: string;
  awardee_phone?: string;
  awardee_email?: string | null;
  notes?: string;
}

export interface TenderAwardeeFilters {
  search?: string;
  page?: number;
  per_page?: number;
}

export interface TenderAwardeeDetail extends TenderAwardee {
  tenders: Tender[];
  call_logs: CallLog[];
  call_statistics: CallStatistics;
  summary?: {
    total_work_order_value?: number;
    converted_tender_count?: number;
  };
}

export const tenderAwardeeService = {
  search: async (q: string, limit = 20): Promise<TenderAwardeeSearchHit[]> => {
    const response = await api.get('/api/tender-awardees/search', { params: { q, limit } });
    return response.data.data || [];
  },

  getAll: async (filters?: TenderAwardeeFilters): Promise<{ data: TenderAwardee[]; total: number }> => {
    const response = await api.get('/api/tender-awardees', { params: filters });
    return { data: response.data.data || [], total: response.data.total ?? 0 };
  },

  getById: async (id: number): Promise<TenderAwardee> => {
    const response = await api.get(`/api/tender-awardees/${id}`);
    return response.data;
  },

  getDetail: async (id: number): Promise<TenderAwardeeDetail> => {
    const response = await api.get(`/api/tender-awardees/${id}/detail`);
    return response.data;
  },

  create: async (data: CreateTenderAwardeeData): Promise<TenderAwardee> => {
    const response = await api.post('/api/tender-awardees', data);
    return response.data.data || response.data;
  },

  update: async (id: number, data: UpdateTenderAwardeeData): Promise<TenderAwardee> => {
    const response = await api.put(`/api/tender-awardees/${id}`, data);
    return response.data.data || response.data;
  },

  merge: async (targetId: number, sourceId: number): Promise<void> => {
    await api.post(`/api/tender-awardees/${targetId}/merge`, { source_id: sourceId });
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/api/tender-awardees/${id}`);
  },
};
